import { BaseService, notifyInactiveBroker } from '../../base';
import type {
  AttachPaymentInfoResponse,
  AttachQuotePayload,
  CalculateProposalResponse,
  GeneratePdfPayload,
  GeneratePdfResponse,
  GetProposalDocumentResponse,
  PaymentInfoPayload,
  Proposal,
} from '../types';

const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;
const PRODUCT_PREFIX = '/portal/v1/homeowner/proposals';

/**
 * Serviço específico para proposta residencial. Concentra os endpoints
 * expostos pelo `HomeownerProposalsController` no backend: calculate,
 * attach-element, pdf, documentos e transmit. A leitura por policyId e a
 * criação inicial são agnósticas de produto e ficam na fachada
 * `proposalsService` / `commonProposalsService`.
 *
 * Compartilha a mesma forma da `BusinessProposalsService`; quem escolhe
 * qual instanciar é o `proposalsService` (fachada) com base no
 * `productCode` conhecido pelo caller.
 */
export class HomeownerProposalsService extends BaseService {
  constructor() {
    super();
  }

  async attachPaymentInfo(
    policyId: string,
    payload: PaymentInfoPayload
  ): Promise<AttachPaymentInfoResponse> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/attach-element`;
      const response = await this.post(path, payload, PORTAL_API_URL);

      if (response.status === 201) {
        const json = await this.handleJsonResponse<unknown>(response);
        return {
          success: true,
          message: json.message,
        };
      }

      const json = await this.handleJsonResponse<unknown>(response);
      return {
        success: false,
        error: json.error || 'Erro ao anexar informações de pagamento',
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao anexar informações de pagamento',
      };
    }
  }

  async updateProposal(
    proposalId: string,
    payload: Partial<AttachQuotePayload>
  ): Promise<{
    success: boolean;
    data?: Proposal;
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(proposalId)}/attach-element`;
      const response = await this.post(path, payload, PORTAL_API_URL);
      return this.handleJsonResponse<Proposal>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao carregar proposta',
      };
    }
  }

  async calculateProposal(
    policyId: string
  ): Promise<CalculateProposalResponse> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/calculate`;
      const response = await this.post(path, {}, PORTAL_API_URL);

      if (!response.ok) {
        const json = await this.handleJsonResponse<unknown>(response);
        return {
          success: false,
          error:
            json.error ||
            `Erro ao calcular proposta (${response.status}: ${response.statusText})`,
        };
      }

      const json = await this.handleJsonResponse<unknown>(response);

      if (!json.success) {
        return {
          success: false,
          error: json.error || 'Erro ao calcular proposta',
        };
      }

      return {
        success: true,
        data: json.data,
        message: json.message,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro inesperado ao calcular proposta',
      };
    }
  }

  async generatePdf(
    proposalId: string,
    payload: GeneratePdfPayload
  ): Promise<{
    success: boolean;
    data?: GeneratePdfResponse;
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(proposalId)}/pdf`;
      const response = await this.post(path, payload, {}, PORTAL_API_URL);

      if (!response.ok) {
        return await extractPdfError(response);
      }

      const data = await this.handleJsonResponse<GeneratePdfResponse>(response);

      if (!data.success || !data.data) {
        return {
          success: false,
          error: data.error || 'Erro ao gerar URL do PDF',
        };
      }

      return { success: true, data: data.data };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao gerar PDF da proposta',
      };
    }
  }

  async getProposalDocument(proposalId: string): Promise<{
    success: boolean;
    data?: GetProposalDocumentResponse;
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(proposalId)}/documents`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<GetProposalDocumentResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar documento da proposta',
      };
    }
  }

  async getInspectionDocument(
    policyId: string,
    type: 'refusal_letter' | 'improvement_letter',
  ): Promise<{ success: boolean; data?: { key: string; url: string }; error?: string }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(policyId)}/documents?type=${encodeURIComponent(type)}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<{ key: string; url: string }>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao buscar documento da proposta',
      };
    }
  }

  async getImprovementLetter(policyId: string): Promise<{
    success: boolean;
    data?: {
      text: string | null;
      attachments: { key: string; url: string }[];
    };
    error?: string;
  }> {
    try {
      const path = `${PRODUCT_PREFIX}/${encodeURIComponent(policyId)}/improvement-letter`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<{
        text: string | null;
        attachments: { key: string; url: string }[];
      }>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar carta de melhoria',
      };
    }
  }

  async transmitProposal(policyId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const sanitizedPolicyId = encodeURIComponent(policyId.trim());
      const path = `${PRODUCT_PREFIX}/${sanitizedPolicyId}/transmit`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<void>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao transmitir proposta',
      };
    }
  }
}

/**
 * Extrai mensagem de erro amigável da resposta falha do PDF. Apenas o
 * campo `error` é realmente usado pelo caller; reaproveita a heurística
 * original (problem+json → message → title → erro array) para não perder
 * contexto de falhas da API.
 */
async function extractPdfError(
  response: Response
): Promise<{ success: false; error: string }> {
  const text = (await response.text()).trim();
  let apiMessage: string | undefined;

  if (text) {
    try {
      const data = JSON.parse(text) as Record<string, unknown>;
      const problemDetail = typeof data.detail === 'string' && data.detail;
      const direct =
        (typeof data.message === 'string' && data.message) ||
        (typeof data.error === 'string' && data.error);
      const problemTitle = typeof data.title === 'string' && data.title;
      const errors = data.errors;

      if (problemDetail) apiMessage = problemDetail;
      else if (direct) apiMessage = direct;
      else if (problemTitle) apiMessage = problemTitle;
      else if (Array.isArray(errors) && errors.length > 0) {
        const first = errors[0] as unknown;
        if (typeof first === 'string') apiMessage = first;
        else if (first && typeof first === 'object') {
          const e = first as Record<string, unknown>;
          const msg = typeof e.message === 'string' && e.message;
          if (msg) apiMessage = msg;
        }
      }
    } catch {
      apiMessage = text;
    }
  }

  // Este caminho não passa pelo `handleJsonResponse` (ex.: PDF): avisa aqui.
  notifyInactiveBroker(response.status, apiMessage);

  return {
    success: false,
    error: apiMessage || `Erro ${response.status}: ${response.statusText}`,
  };
}

export const homeownerProposalsService = new HomeownerProposalsService();
